import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Send, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { supabase } from '../../lib/common/supabase';
import { cn } from '../../lib/common/utils';

import { 
  getPageNames as getJobSeekerPageNames, 
  getComponentsForPages as getJobSeekerComponentsForPages, 
  getQuestionsForComponents as getJobSeekerQuestionsForComponents
} from '../../lib/jobseeker/betaTestData';

import { 
  getPageNames as getEmployerPageNames, 
  getComponentsForPages as getEmployerComponentsForPages, 
  getQuestionsForComponents as getEmployerQuestionsForComponents
} from '../../lib/employer/betaTestData';

interface BetaTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userRole: string;
}

type Step = 'initial' | 'pages' | 'components' | 'questions';

export function BetaTestModal({
  isOpen,
  onClose,
  userId,
  userName,
  userRole
}: BetaTestModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>('initial');
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExiting, setIsExiting] = useState(false);
const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quickFeedback, setQuickFeedback] = useState<string>('');

const modalRef = useRef<HTMLDivElement>(null);

  const [userEmail, setUserEmail] = useState<string>('');

  const getPageNames = userRole === 'employer' ? getEmployerPageNames : getJobSeekerPageNames;
  const getComponentsForPages = userRole === 'employer' ? getEmployerComponentsForPages : getJobSeekerComponentsForPages;
  const getQuestionsForComponents = userRole === 'employer' ? getEmployerQuestionsForComponents : getJobSeekerQuestionsForComponents;

useEffect(() => {
  const fetchUserEmail = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email) {
      setUserEmail(user.email);
    }
  };
  
  if (isOpen && userId) {
    fetchUserEmail();
  }
}, [isOpen, userId]);

useEffect(() => {
    if (isOpen) {
      setCurrentStep('initial');
      setSelectedPages([]);
      setSelectedComponents([]);
      setResponses({});
      setError(null);
      setCurrentQuestionIndex(0);
      setQuickFeedback('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
      setIsExiting(false);
    }, 300);
  };

  const handlePageSelection = (pageId: string) => {
    setSelectedPages(prev => 
      prev.includes(pageId) 
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const handleComponentSelection = (componentId: string) => {
    setSelectedComponents(prev => 
      prev.includes(componentId) 
        ? prev.filter(id => id !== componentId)
        : [...prev, componentId]
    );
  };

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const allQuestions = getQuestionsForComponents(selectedComponents);
  const flatQuestions = allQuestions.flatMap(component => 
    component.questions.map(q => ({ ...q, componentId: component.id, componentName: component.name }))
  );
  const currentQuestion = flatQuestions[currentQuestionIndex];

  const handleNextQuestion = () => {
    if (currentQuestionIndex < flatQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

const handleSubmit = async (isQuickFeedback: boolean = false) => {
    setIsSubmitting(true);
    setError(null);

    try {
    
      const formattedResponses = isQuickFeedback ? {
        responses: [{
          componentId: 'quick-feedback',
          componentName: 'Quick Feedback',
          questions: [{
            questionId: 'quick-feedback-1',
            question: 'Quick Feedback',
            answer: quickFeedback
          }]
        }]
      } : {
        responses: allQuestions.map(component => ({
          componentId: component.id,
          componentName: component.name,
          questions: component.questions.map(q => ({
            questionId: q.id,
            question: q.question,
            answer: responses[q.id] || ''
          }))
        }))
      };

 
      const tableName = userRole === 'employer' ? 'employer_beta_test_responses' : 'beta_test_responses';
      
      const { error: insertError } = await supabase
        .from(tableName)
        .insert({
          user_id: userId,
          selected_pages: isQuickFeedback ? [] : selectedPages,
          selected_components: isQuickFeedback ? [] : selectedComponents,
          responses: formattedResponses,
          user_role: userRole,
          browser_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language
          },
          feedback_type: isQuickFeedback ? 'quick' : 'detailed'
        });

      if (insertError) throw insertError;

   
      const { data: { session } } = await supabase.auth.getSession();
      
     const functionName = userRole === 'employer' ? 'notify-employer-beta-test' : 'notify-beta-test';
      
 await supabase.functions.invoke(functionName, {
        body: {
          userId,
          userEmail,
          userName,
          userRole,
          selectedPages: isQuickFeedback ? [] : selectedPages,
          selectedComponents: isQuickFeedback ? [] : selectedComponents,
          responses: formattedResponses
        },
        headers: {
          Authorization: `Bearer ${session?.access_token || ''}`
        }
      });

     
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-20 right-4 bg-green-900/90 text-green-100 px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-in';
      successDiv.innerHTML = `
        <div class="flex items-center space-x-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <span>Beta test feedback submitted successfully. Thank you!</span>
        </div>
      `;
      document.body.appendChild(successDiv);

      setTimeout(() => {
        successDiv.classList.add('animate-slide-out');
        setTimeout(() => document.body.removeChild(successDiv), 300);
      }, 3000);

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && !isExiting && (
        <motion.div
          key="beta-test-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            ref={modalRef}
            key="beta-test-modal-content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-black border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
           
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-light text-white">Beta Test Feedback</h2>
              <button
                onClick={handleClose}
                className="text-white/40 hover:text-white/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

           
            <div className="flex-1 overflow-y-auto p-6">
{error && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {currentStep === 'initial' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-light text-white mb-2 tracking-tight">Share Your Feedback</h3>
                    <p className="text-sm text-white/40">Quick feedback or detailed survey - your choice</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
                      <h4 className="text-lg font-medium text-white mb-3">Quick Feedback</h4>
                      <p className="text-sm text-white/60 mb-4">Have something specific to share? Type it below and submit.</p>
                      <textarea
                        value={quickFeedback}
                        onChange={(e) => setQuickFeedback(e.target.value)}
                        placeholder="Share your thoughts, suggestions, or report issues..."
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 resize-none h-32 focus:outline-none focus:border-white/20 transition-all"
                      />
                      <button
                        onClick={() => handleSubmit(true)}
                        disabled={!quickFeedback.trim() || isSubmitting}
                        className="mt-4 w-full px-6 py-2.5 rounded-full bg-white text-black font-light hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Submit Quick Feedback
                      </button>
                    </div>
                    
                    <div className="text-center py-4">
                      <div className="flex items-center space-x-4">
                        <div className="h-px flex-1 bg-white/10" />
                        <span className="text-xs text-white/40 uppercase tracking-wider">or</span>
                        <div className="h-px flex-1 bg-white/10" />
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setCurrentStep('pages')}
                      className="w-full p-6 rounded-xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <h4 className="text-lg font-medium text-white mb-1">Take Detailed Survey</h4>
                          <p className="text-sm text-white/60">Help us understand your experience with specific features</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-white/60 transition-colors" />
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 'pages' && (
  <div className="space-y-6">
    <div className="text-center">
      <h3 className="text-2xl font-light text-white mb-2 tracking-tight">Which pages did you explore?</h3>
      <p className="text-sm text-white/40">Help us improve your experience</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {getPageNames().map((page, index) => (
        <motion.button
          key={page.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05, duration: 0.3 }}
          onClick={() => handlePageSelection(page.id)}
          className={cn(
            "relative p-4 rounded-2xl transition-all duration-300 group",
            selectedPages.includes(page.id)
              ? "bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg"
              : "bg-white/[0.02] backdrop-blur border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/10"
          )}
        >
          <div className="flex flex-col items-start space-y-2">
            <div className={cn(
              "absolute top-3 right-3 w-5 h-5 rounded-full border transition-all duration-300",
              selectedPages.includes(page.id)
                ? "bg-white border-white scale-100"
                : "border-white/20 scale-90 group-hover:scale-100 group-hover:border-white/40"
            )}>
              <motion.div
                initial={false}
                animate={{ scale: selectedPages.includes(page.id) ? 1 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="w-full h-full flex items-center justify-center"
              >
                <Check className="w-3 h-3 text-black" />
              </motion.div>
            </div>
            <span className={cn(
              "text-sm font-medium transition-colors duration-300",
              selectedPages.includes(page.id) ? "text-white" : "text-white/70 group-hover:text-white/90"
            )}>
              {page.name}
            </span>
          </div>
        </motion.button>
      ))}
    </div>
    <div className="flex justify-center">
      <div className="flex items-center space-x-1.5">
        {getPageNames().map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-1 rounded-full transition-all duration-300",
              index < selectedPages.length ? "w-8 bg-white/60" : "w-1 bg-white/20"
            )}
          />
        ))}
      </div>
    </div>
  </div>
)}

              {currentStep === 'components' && (
  <div className="space-y-8">
    <div className="text-center">
      <h3 className="text-2xl font-light text-white mb-2 tracking-tight">What did you test?</h3>
      <p className="text-sm text-white/40">Your feedback shapes our product</p>
    </div>
    {getComponentsForPages(selectedPages).map((page, pageIndex) => (
      <motion.div
        key={page.pageId}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: pageIndex * 0.1, duration: 0.3 }}
        className="space-y-4"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <h4 className="text-sm font-medium text-white/60 px-3">{page.pageName}</h4>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
        <div className="grid gap-2">
          {page.components.map((component, index) => (
            <motion.button
              key={component.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: pageIndex * 0.1 + index * 0.03, duration: 0.3 }}
              onClick={() => handleComponentSelection(component.id)}
              className={cn(
                "relative p-4 rounded-xl transition-all duration-300 text-left group",
                selectedComponents.includes(component.id)
                  ? "bg-gradient-to-r from-white/10 to-white/[0.05] backdrop-blur-xl border border-white/20 shadow-lg"
                  : "bg-white/[0.02] backdrop-blur border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/10"
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn(
                  "text-sm font-normal transition-colors duration-300",
                  selectedComponents.includes(component.id) ? "text-white" : "text-white/70 group-hover:text-white/90"
                )}>
                  {component.name}
                </span>
                <div className={cn(
                  "w-5 h-5 rounded-full border transition-all duration-300 flex items-center justify-center",
                  selectedComponents.includes(component.id)
                    ? "bg-white border-white"
                    : "border-white/20 group-hover:border-white/40"
                )}>
                  <motion.div
                    initial={false}
                    animate={{ scale: selectedComponents.includes(component.id) ? 1 : 0, opacity: selectedComponents.includes(component.id) ? 1 : 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    <Check className="w-3 h-3 text-black" />
                  </motion.div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    ))}
    <div className="flex justify-center pt-4">
      <div className="px-4 py-2 rounded-full bg-white/5 border border-white/10">
        <span className="text-xs text-white/60">{selectedComponents.length} component{selectedComponents.length !== 1 ? 's' : ''} selected</span>
      </div>
    </div>
  </div>
)}

              {currentStep === 'questions' && currentQuestion && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-white">{currentQuestion.componentName}</h3>
                      <span className="text-sm text-white/60">
                        Question {currentQuestionIndex + 1} of {flatQuestions.length}
                      </span>
                    </div>
                    <p className="text-white/90 mb-4">{currentQuestion.question}</p>
                    
                   {currentQuestion.inputType === 'dropdown' && currentQuestion.dropdownOptions && (
  <div className="space-y-2">
    {currentQuestion.dropdownOptions.map((option, index) => (
      <label
        key={index}
        className={cn(
          "flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all",
          responses[currentQuestion.id] === option
            ? "bg-white/10 border-white/30"
            : "bg-white/5 border-white/10 hover:bg-white/10"
        )}
      >
        <input
          type="radio"
          name={currentQuestion.id}
          value={option}
          checked={responses[currentQuestion.id] === option}
          onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
          className="sr-only"
        />
        <div className={cn(
          "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all mt-0.5 flex-shrink-0",
          responses[currentQuestion.id] === option
            ? "bg-white border-white"
            : "border-white/30"
        )}>
          {responses[currentQuestion.id] === option && (
            <div className="w-2.5 h-2.5 rounded-full bg-black" />
          )}
        </div>
        <span className="text-white/90 text-sm">{option}</span>
      </label>
    ))}
    
  
    <label
     className={cn(
  "flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all",
  !!responses[currentQuestion.id] && 
  !currentQuestion.dropdownOptions.includes(responses[currentQuestion.id])
    ? "bg-white/10 border-white/30"
    : "bg-white/5 border-white/10 hover:bg-white/10"
)}
    >
      <input
  type="radio"
  name={currentQuestion.id}
  value="__other__"
  checked={!!responses[currentQuestion.id] && 
    !currentQuestion.dropdownOptions.includes(responses[currentQuestion.id])}
  onChange={() => handleResponseChange(currentQuestion.id, '__other__')}
  className="sr-only"
/>
      <div className={cn(
  "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all mt-0.5 flex-shrink-0",
  !!responses[currentQuestion.id] && 
  !currentQuestion.dropdownOptions.includes(responses[currentQuestion.id])
    ? "bg-white border-white"
    : "border-white/30"
)}>
  {!!responses[currentQuestion.id] && 
   !currentQuestion.dropdownOptions.includes(responses[currentQuestion.id]) && (
    <div className="w-2.5 h-2.5 rounded-full bg-black" />
  )}
</div>
      <span className="text-white/90 text-sm">Other</span>
    </label>
    
   
{!!responses[currentQuestion.id] && 
 !currentQuestion.dropdownOptions.includes(responses[currentQuestion.id]) && (
  <div className="mt-3 ml-8">
    <textarea
      value={responses[currentQuestion.id] === '__other__' ? '' : responses[currentQuestion.id]}
      onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
      placeholder="Please specify..."
      className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 resize-none h-20 focus:outline-none focus:border-white/20 transition-all"
      autoFocus
    />
  </div>
)}
  </div>
)}

                    
                  </div>
                </div>
              )}
            </div>

         
<div className="p-6 border-t border-white/10 flex justify-between">
              <div className="flex gap-3">
                {currentStep === 'pages' && (
                  <button
                    onClick={() => setCurrentStep('initial')}
                    className="px-6 py-2.5 rounded-full border border-white/20 text-white font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
                {currentStep === 'components' && (
                  <button
                    onClick={() => setCurrentStep('pages')}
                    className="px-6 py-2.5 rounded-full border border-white/20 text-white font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
                {currentStep === 'questions' && currentQuestionIndex > 0 && (
                  <button
                    onClick={handlePreviousQuestion}
                    className="px-6 py-2.5 rounded-full border border-white/20 text-white font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                )}
                {currentStep === 'questions' && currentQuestionIndex === 0 && (
                  <button
                    onClick={() => setCurrentStep('components')}
                    className="px-6 py-2.5 rounded-full border border-white/20 text-white font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 rounded-full border border-white/20 text-white font-light hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                
                {currentStep === 'pages' && (
                  <button
                    onClick={() => setCurrentStep('components')}
                    disabled={selectedPages.length === 0}
                    className="px-6 py-2.5 rounded-full bg-white text-black font-light hover:bg-white/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                
                {currentStep === 'components' && (
                  <button
                    onClick={() => setCurrentStep('questions')}
                    disabled={selectedComponents.length === 0}
                    className="px-6 py-2.5 rounded-full bg-white text-black font-light hover:bg-white/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                
                {currentStep === 'questions' && currentQuestionIndex < flatQuestions.length - 1 && (
                  <button
                    onClick={handleNextQuestion}
                    className="px-6 py-2.5 rounded-full bg-white text-black font-light hover:bg-white/90 transition-colors flex items-center gap-2"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                
{currentStep === 'questions' && currentQuestionIndex === flatQuestions.length - 1 && (
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={isSubmitting || Object.keys(responses).length < flatQuestions.length}
                    className="px-6 py-2.5 rounded-full bg-white text-black font-light hover:bg-white/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Submit Feedback
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}